import { useState } from "react";
import {
  deriveAuthSecret,
  derivePasswordWrapKey,
  deriveRecoveryAuthProof,
  deriveRecoveryWrapKey,
  generateChatKey,
  generateMnemonic,
  generateSalt,
  wrapChatKey
} from "../lib/crypto.js";
import { registerAccount } from "../lib/api.js";
import { saveSession, type Session } from "../lib/session.js";

interface SetupProps {
  onSwitchToLogin: () => void;
  onCreated: (session: Session) => void;
}

type Step = "form" | "mnemonic";

/**
 * One-time account creation (Decision 4). Only reachable/relevant while no
 * account exists yet — the server enforces that with a 403 regardless, so a
 * duplicate Setup attempt from a stale browser tab just gets redirected to
 * Login with an explanatory message.
 *
 * Revision 5 (Decisions 4, 11): `chatKey` is now a random key generated
 * here, wrapped two independent ways before it ever leaves this device —
 * once under the password (`passwordWrapKey`), once under a freshly
 * generated 12-word recovery mnemonic (`recoveryWrapKey`). The mnemonic is
 * shown exactly once, after registration succeeds, and is never sent to or
 * retrievable from the server in any recoverable form.
 */
export default function Setup({ onSwitchToLogin, onCreated }: SetupProps) {
  const [step, setStep] = useState<Step>("form");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [status, setStatus] = useState<"idle" | "working" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mnemonic, setMnemonic] = useState<string | null>(null);
  const [confirmedSaved, setConfirmedSaved] = useState(false);
  const [pendingSession, setPendingSession] = useState<Session | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrorMessage(null);

    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setStatus("working");
    try {
      const salt = generateSalt();
      const chatKey = generateChatKey();
      const newMnemonic = generateMnemonic();

      const [authSecret, passwordWrapKey, recoveryAuthProof, recoveryWrapKey] = await Promise.all([
        deriveAuthSecret(password, salt),
        derivePasswordWrapKey(password, salt),
        deriveRecoveryAuthProof(newMnemonic, salt),
        deriveRecoveryWrapKey(newMnemonic, salt)
      ]);

      const wrappedChatKeyPassword = wrapChatKey(chatKey, passwordWrapKey);
      const wrappedChatKeyRecovery = wrapChatKey(chatKey, recoveryWrapKey);

      const result = await registerAccount({
        username: username.trim(),
        authSecret,
        recoveryAuthProof,
        wrappedChatKeyPassword,
        wrappedChatKeyRecovery,
        salt,
        deviceType: "web",
        deviceName: "PC browser",
        inviteCode: inviteCode.trim() || undefined
      });

      const session: Session = {
        username: result.username,
        deviceId: result.deviceId,
        token: result.deviceToken,
        chatKey
      };
      setPendingSession(session);
      setMnemonic(newMnemonic);
      setStep("mnemonic");
      setStatus("idle");
    } catch (err) {
      const message = err instanceof Error ? err.message : "setup_failed";
      const friendly: Record<string, string> = {
        invite_code_required: "This deploy already has an account — an invite code from an existing account is required.",
        invalid_or_expired_invite_code: "That invite code is invalid, already used, or expired.",
        max_accounts_reached: "This deploy has reached its maximum number of accounts."
      };
      setErrorMessage(friendly[message] ?? message);
      setStatus("error");
    }
  }

  function handleContinue() {
    if (!pendingSession || !confirmedSaved) return;
    saveSession(pendingSession);
    onCreated(pendingSession);
  }

  function downloadBlob(content: string, filename: string, mimeType: string) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function handleDownloadTxt() {
    if (!mnemonic) return;
    downloadBlob(mnemonic, "bridge-recovery-key.txt", "text/plain");
  }

  function handleDownloadJson() {
    if (!mnemonic) return;
    const payload = {
      app: "Bridge",
      type: "recovery-mnemonic",
      words: mnemonic.split(" "),
      createdAt: new Date().toISOString()
    };
    downloadBlob(JSON.stringify(payload, null, 2), "bridge-recovery-key.json", "application/json");
  }

  if (step === "mnemonic" && mnemonic) {
    const words = mnemonic.split(" ");
    return (
      <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
        <h1 className="text-xl font-semibold text-slate-100">Save your recovery phrase</h1>
        <p className="mt-2 font-semibold text-red-400">
          Write these 12 words down somewhere safe and offline. They will never be shown again — this
          is the only way to recover access if you forget your password.
        </p>
        <div className="mt-4 grid grid-cols-3 gap-2 rounded-lg border border-slate-700 bg-[#0b1220] p-4">
          {words.map((word, i) => (
            <div key={i} className="text-sm text-slate-200">
              <span className="mr-1 text-slate-500">{i + 1}.</span>
              {word}
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={handleDownloadTxt}
            className="cursor-pointer rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-sm text-slate-200"
          >
            Download as .txt
          </button>
          <button
            type="button"
            onClick={handleDownloadJson}
            className="cursor-pointer rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-sm text-slate-200"
          >
            Download as .json
          </button>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={confirmedSaved} onChange={(e) => setConfirmedSaved(e.target.checked)} />
          I have saved this recovery phrase somewhere safe and offline.
        </label>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!confirmedSaved}
          className="mt-4 w-full cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          Continue to Bridge
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
      <h1 className="text-xl font-semibold text-slate-100">Create your Bridge account</h1>
      <p className="mt-2 text-sm text-slate-300">
        One-time setup — Bridge stays single-user in v1. Your password never leaves this device;
        only a one-way derived secret is sent to the server to authenticate future logins.
      </p>
      <p className="mt-2 text-sm font-semibold text-amber-400">
        After you create your account, you'll be shown a 12-word recovery phrase exactly once — save
        it offline. It's the only way back in if you forget your password.
      </p>
      <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Username
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            placeholder="e.g. alex"
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Confirm password
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-slate-300">
          Invite code (leave blank for the very first account on this deploy)
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            autoComplete="off"
            placeholder="Only required if an account already exists"
            className="w-full rounded-lg border border-slate-700 bg-[#0b1220] px-3 py-2 text-slate-200"
          />
        </label>
        <button
          type="submit"
          disabled={status === "working" || !username.trim() || !password}
          className="cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "working" ? "Creating account…" : "Create account"}
        </button>
        {status === "error" && errorMessage && <p className="text-sm text-red-400">{errorMessage}</p>}
      </form>
      <p className="mt-3 text-sm text-slate-400">
        Already have an account?{" "}
        <button type="button" onClick={onSwitchToLogin} className="cursor-pointer border-none bg-transparent p-0 text-blue-400">
          Log in
        </button>
      </p>
    </div>
  );
}
